import { useCallback, useMemo } from 'react'
import { toSeconds } from 'duration-fns'
import { useBoolean, useCounter, useInterval } from 'usehooks-ts'

import { getTokenFromStorage } from '@sputnik/auth'
import { getExpirationFromToken } from '@sputnik/auth/jwt'

const countdownInterval = 1 * 1000

export type CountdownControllers = {
  startCountdown: () => void
  stopCountdown: () => void
  resetCountdown: (newValue?: number) => void
}

export const useAuthTimeoutCountdown = (): [number, CountdownControllers] => {
  const initialAuthDuration = useMemo(() => {
    const token = getTokenFromStorage()
    const tokenExpirationTime = getExpirationFromToken(token)

    return Math.ceil(
      toSeconds((tokenExpirationTime ? tokenExpirationTime * 1000 : new Date().valueOf()) - new Date().valueOf()),
    )
  }, [])

  const { count, decrement, setCount, reset: resetCounter } = useCounter(initialAuthDuration)

  const { value: isCountdownRunning, setTrue: startCountdown, setFalse: stopCountdown } = useBoolean(true)

  const resetCountdown = useCallback(
    (newValue?: number) => {
      stopCountdown()
      if (newValue) {
        setCount(newValue)
      } else {
        resetCounter()
      }
    },
    [stopCountdown, resetCounter, setCount],
  )

  const countdownCallback = useCallback(() => {
    if (count === 0) {
      stopCountdown()
      return
    }

    if (isCountdownRunning) {
      decrement()
    }
  }, [count, decrement, isCountdownRunning, stopCountdown])

  useInterval(countdownCallback, isCountdownRunning ? countdownInterval : null)

  return [count, { startCountdown, stopCountdown, resetCountdown }]
}
